/**
 * Setlist state - what OPSetlist last told OPBridge, and what OPBridge
 * actually sent to Live as a result.
 *
 * # Why this exists: two tiles were CONFIDENTLY WRONG, not merely stale
 *
 * The operator's report, 2026-09-19: on an OPSetlist song change the
 * Transpose and Waves tiles kept lighting the PREVIOUS song's values. Not
 * blank - wrong. `recQuant` updated because it is a real readback riding
 * `/clock/status`; the Transpose row read `localStorage` and only ever SENT,
 * and the Waves key was a session signal whose only writers were a tile tap
 * or a Rig State recall. Nothing external could relight either, and neither
 * could even render "unknown", because knowing you are stale requires
 * knowing something changed.
 *
 * Their framing is the bar this module is built to: *"i'll be changing
 * things thru opbuttons/opsetlist/opbridge, not thru ableton, so the only
 * reason i'm pushing for that other 1% is to claim that opbuttons is a live
 * rep of ableton, not what it thinks it is."*
 *
 * # ⚠️ THE CONTRACT TRAP - `null` is not zero and not unknown
 *
 * Straight from OPBridge S276, and it is the thing most likely to make the
 * tiles wrong again in a new way:
 *
 * **`current.keys_trans: null` means "this song carried no transpose, so
 * Ableton was left alone."** All four legs of a song change are
 * independently conditional, and an absent field is not sent at all - which
 * makes every value **STICKY ACROSS SONGS**. A song with a transpose
 * followed by a song without one leaves Live where the first one put it.
 *
 * So the two fields answer different questions and the tile wants the
 * second one:
 *
 * - `current.keys_trans` - *what did THIS SONG ask for.*
 * - `last_sent.keys_trans` - *what did Live actually RECEIVE from us.*
 *
 * Given "a live rep of ableton", **`last_sent` is what the Transpose tile
 * lights from.**
 *
 * # Waves is structurally different and it is not an oversight
 *
 * `last_sent` carries `bpm`, `keys_trans`, `time_sig`, `quant` and
 * `at_unix_ms` - and deliberately NOT `vocals_key`. Waves state is not
 * queryable by anything, so there is no "what the plugin actually has" to
 * report, and that ceiling is real and structural.
 *
 * What there IS (OPBridge S279, live on both machines since v1.15.132, first
 * seen populated on the Mac 2026-09-28) is `vocals_key.last`: what OPBridge's
 * autotune leg DID with the last keyed song - its own parse of the key and
 * whether the two CCs left the port. {@link songAutotune} reads that, and it
 * replaced `vocalsKey.ts`, a port of OPBridge's parser that had to be kept in
 * step by hand (retired 2026-09-28 after agreeing on all 23 of OPBridge's own
 * test vectors and the live Mac sample, and DISAGREEING on one input a port
 * can never get right: a leading U+FEFF, which JS `trim()` strips and Rust's
 * does not).
 */

import { createSignal, type Accessor } from 'solid-js';
import type { SetlistCurrent, SetlistStateResponse, SetlistVocalsKeyState } from '../opbridge/httpClient.ts';

/** The Waves scale posture a key carries, in OPBridge's words (`Mode::name`). */
export type VocalsKeyMode = 'major' | 'minor' | 'chromatic';

/** How well we know the setlist state. Same shape as the other readback
 *  mirrors, and for the same reason: "never asked" and "asked and it is
 *  empty" send the operator to different places. */
export type SetlistStatus = 'unknown' | 'ok' | 'unauthorized' | 'error';

export interface SetlistMirror {
  status: SetlistStatus;
  /** What the CURRENT song asked for. `null` fields mean the song carried no
   *  value for that leg, so Live was left alone - NOT that it is zero. */
  current: SetlistStateResponse['current'] | null;
  /** What OPBridge last actually SENT to Live. This is the "live rep of
   *  Ableton" half, and it is what a transpose readout must use. */
  lastSent: SetlistStateResponse['last_sent'] | null;
  /** OPBridge's `vocals_key` block: is an autotune port configured, and what
   *  its autotune leg did with the last keyed song. Read by {@link songAutotune}. */
  autotune: SetlistVocalsKeyState | null;
  /** Monotonic write counter, on both the poll body and the push frame.
   *  `0` means nothing has ever been written. */
  generation: number;
  /** `Date.now()` of the last completed read, 0 if never. */
  at: number;
  /** The song that last MOVED THE RIG, which with auto-sync off is not
   *  `current`: OPSetlist reports every song the operator scrolls to, and
   *  OPBridge records each as `current` with `sent: false` and sends nothing.
   *  `null` when no song is known to have moved it. See {@link appliedSong}. */
  applied: SetlistCurrent | null;
  /** `Date.now()` when THIS app first saw the rig moved by {@link applied}, 0
   *  if never. Orders a song against a local tap (newest wins). Neither a
   *  re-poll nor a scroll to a held song moves it - before 2026-10-15 a
   *  scroll did (it was stamped per `generation`), and every scroll erased the
   *  operator's tap from the Waves row while the plugin still had it. */
  appliedAt: number;
  /** Raw `sent` from the body: `null` when absent. */
  sent: boolean | null;
  /** {@link sendStamp} of the last body, so a new send is told from a re-poll. */
  sendStampKey: string;
  /** `current` was ADOPTED from a peer bridge (`current_origin_node_id`): this bridge never sent it to Live, so its
   *  own `last_sent` says nothing about what Live has (2026-10-16, measured: null on the dev PC). */
  viaPeer: boolean;
  detail?: string;
}

const EMPTY: SetlistMirror = {
  status: 'unknown', current: null, lastSent: null, autotune: null, generation: 0, at: 0,
  applied: null, appliedAt: 0, sent: null, sendStampKey: '', viaPeer: false,
};

/** Identity of the last real sends: OPBridge stamps `last_sent` on every send
 *  to Live and `vocals_key.last` on every autotune attempt, and a held song
 *  touches neither. */
function sendStamp(body: SetlistStateResponse): string {
  return `${body.last_sent?.at_unix_ms ?? ''}|${autotuneOf(body)?.last?.at_unix_ms ?? ''}`;
}

/** A song adopted from a peer whose bridge says what the ORIGIN did (OPBridge S324: `origin_sent`). */
function originKnown(body: SetlistStateResponse): boolean {
  return typeof body.current_origin_node_id === 'string' && body.current_origin_node_id.length > 0 && typeof body.origin_sent === 'boolean';
}
/** Whether `current` went to Live: on this bridge, or for an adopted song, on the origin (S324). */
function sentOf(body: SetlistStateResponse): boolean | undefined {
  return originKnown(body) ? body.origin_sent as boolean : body.sent;
}
/** The autotune block that speaks for `current`: the origin's last outcome for an adopted song when the peer says it
 *  (S324), else this bridge's own. */
function autotuneOf(body: SetlistStateResponse): SetlistStateResponse['vocals_key'] {
  if (originKnown(body) && body.origin_last_autotune !== undefined) return { ...(body.vocals_key ?? {}), last: body.origin_last_autotune ?? null };
  return body.vocals_key;
}

/**
 * Did this frame move the rig to its `current` song, given the previous one?
 *
 * - **A send stamp changed** - OPBridge sent this song (auto-sync, or Sync).
 *   Covers an autotune leg that went out while Live's OSC send failed.
 * - **`sent` is true on a new generation, or flipped to true** - the frame
 *   that reports a send. `sent` absent (never seen from a real OPBridge, but
 *   every older smoke frame) counts as sent, which is the pre-2026-10-15 rule.
 * - **A new song adopted from a peer bridge** (`current_origin_node_id`). Since
 *   OPBridge S324 the peer says whether the ORIGIN sent it (`origin_sent`) and
 *   its autotune outcome, so it is judged exactly like a local song: held on
 *   the origin, nothing changed; sent there, it did. From an older peer (no
 *   `origin_sent`) it counts as a song change, as it always has.
 */
function frameApplies(body: SetlistStateResponse, prev: SetlistMirror, gen: number): boolean {
  const known = prev.status === 'ok';
  const newGen = !known || gen !== prev.generation;
  if (known && sendStamp(body) !== prev.sendStampKey) return true;
  if (sentOf(body) !== false && (newGen || prev.sent === false)) return true;
  return newGen && !originKnown(body) && typeof body.current_origin_node_id === 'string' && body.current_origin_node_id.length > 0;
}

const [mirrorSig, setMirrorSig] = createSignal<SetlistMirror>(EMPTY);

/** The whole mirror. Reactive. */
export const setlistState: Accessor<SetlistMirror> = mirrorSig;

/**
 * Apply a poll body or a push frame.
 *
 * **`generation` gates the write, and that is load-bearing.** OPBridge's
 * `watch` channel coalesces by design, so two rapid song changes can produce
 * one frame and **arrival order is not reliable**. A poll response and a push
 * frame can also be in flight together. Comparing `generation` is the only
 * way to avoid an older snapshot overwriting a newer one - which would
 * reintroduce exactly the confidently-wrong tile this module exists to fix.
 *
 * Equal generations are allowed through: the same generation carries the same
 * data, so a rewrite is harmless, and refusing it would drop the legitimate
 * second frame of a song change (`record_song_change` then `mark_sent` are
 * both publish points, so TWO frames per change is normal - the second is the
 * one carrying the populated `last_sent`).
 */
export function recordSetlistState(body: SetlistStateResponse): void {
  const gen = typeof body.generation === 'number' ? body.generation : 0;
  const prev = mirrorSig();
  if (prev.status === 'ok' && gen < prev.generation) return;   // stale, drop it
  const now = Date.now();
  const current = body.current ?? null;
  let applied = prev.applied;
  let appliedAt = prev.appliedAt;
  if (frameApplies(body, prev, gen)) {
    applied = current;
    appliedAt = now;
  } else if (prev.status !== 'ok') {
    // First read lands on a HELD song (auto-sync off, scrolled before this app
    // opened): the song that moved the rig was never seen. OPBridge's last
    // autotune outcome is still what the plugin was last sent, so stand it in
    // as that song's key; with none, nothing is known and nothing lights.
    const last = autotuneOf(body)?.last;
    applied = last ? { vocals_key: last.vocals_key } : null;
    appliedAt = now;
  }
  setMirrorSig({
    status: 'ok',
    current,
    lastSent: body.last_sent ?? null,
    autotune: autotuneOf(body) ?? null,
    generation: gen,
    at: now,
    applied,
    appliedAt,
    sent: typeof sentOf(body) === 'boolean' ? sentOf(body) as boolean : null,
    sendStampKey: sendStamp(body),
    viaPeer: typeof body.current_origin_node_id === 'string' && body.current_origin_node_id.length > 0,
  });
}

/** The song whose values the rig has: `current` once it was sent, the last
 *  sent song while `current` is held. Reactive. */
export function appliedSong(m: SetlistMirror = mirrorSig()): SetlistCurrent | null {
  return m.status === 'ok' ? m.applied : null;
}

/** Record a failed read, preserving WHICH failure. */
export function recordSetlistFailure(
  status: Exclude<SetlistStatus, 'unknown' | 'ok'>,
  detail: string,
): void {
  setMirrorSig({ ...EMPTY, status, at: Date.now(), detail });
}

/** Drop the mirror. Tests + sign-out. */
export function resetSetlistState(): void {
  setMirrorSig(EMPTY);
}

/**
 * The transpose Live actually has, or `null` when we cannot say.
 *
 * ⚠️ **`null` must render as `--`, never as `0`.** Zero is a REAL transpose
 * (the song explicitly asked for concert pitch) and is indistinguishable
 * from "we do not know" once it has been defaulted. This is the same rule
 * PLAN records for the whole transpose arc, and defaulting is the single
 * easiest way to put this feature back where it started.
 *
 * Reads `last_sent`, not `current`, per the module header - `current` is
 * this song's INTENT and goes null on a song that carried no transpose,
 * while Live is still sitting at whatever the previous song set.
 */
export function observedTranspose(m: SetlistMirror = mirrorSig()): number | null {
  if (m.status !== 'ok') return null;
  // A song adopted from a peer (2026-10-16): this bridge never sends to Live, so its `last_sent` is null or stale.
  // What Live has is the APPLIED song's transpose: the adopted song once its origin sent it (OPBridge S324
  // `origin_sent`), the one before while it is only held there (`frameApplies`).
  if (m.viaPeer) {
    const a = m.applied?.keys_trans;
    return typeof a === 'number' ? a : null;
  }
  const v = m.lastSent?.keys_trans;
  return typeof v === 'number' ? v : null;
}

/**
 * Rust's `str::trim`: strips Unicode `White_Space` only. NOT JS `trim()`, which
 * also strips U+FEFF and does not strip U+0085. OPBridge stores
 * `vocals_key.last.vocals_key` Rust-trimmed, so joining the current song to it
 * with the JS trim would mis-match on exactly those two characters.
 */
const RUST_WHITE_SPACE = '\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000';
const RUST_TRIM = new RegExp(`^[${RUST_WHITE_SPACE}]+|[${RUST_WHITE_SPACE}]+$`, 'g');
export function rustTrim(s: string): string {
  return s.replace(RUST_TRIM, '');
}

const MODES: readonly VocalsKeyMode[] = ['major', 'minor', 'chromatic'];

/**
 * The key and scale the APPLIED song put on the Waves plugin, or `null`.
 *
 * "Applied", not current ({@link appliedSong}): with auto-sync off, scrolling
 * OPSetlist moves `current` and sends nothing, so the plugin still has the
 * last SENT song's key. Reading `current` here lit a scrolled-to song only when
 * its key text happened to equal the last sent one (operator, 2026-10-15: "on
 * a small percentage of the songs, they appear highlighted"). Below, "the
 * song" means the applied song.
 *
 * From OPBridge's own resolution (`vocals_key.last`), never our parse: it is
 * the thing that moves the plugin, so its outcome is the only honest source.
 * Lit only when ALL of these hold, and each one closes a real lie:
 *
 * 1. **The song carries a key.** A keyless song leaves the plugin
 *    alone and lights nothing here (DEC-217, "no key -> nothing lit",
 *    unchanged), even though OPBridge's `last` still holds the previous key.
 * 2. **`last` is THIS key** - its `vocals_key` equals the song's,
 *    trimmed the way OPBridge trims ({@link rustTrim}). Otherwise `last` is an
 *    earlier song's outcome: a peer-adopted song, or the moment between
 *    the song-change frame and the autotune frame. An identical earlier key is
 *    fine to light - the plugin has it either way.
 * 3. **OPBridge parsed it** (`pitch_class` and a known `mode`).
 * 4. **The CCs left** (`emitted`). With no autotune port configured, OPBridge
 *    parses and sends nothing; the port this replaced lit the key anyway.
 */
export function songAutotune(m: SetlistMirror = mirrorSig()): { pitchClass: number; mode: VocalsKeyMode } | null {
  const s = songAutotuneStatus(m);
  return s.kind === 'sent' ? { pitchClass: s.pitchClass, mode: s.mode } : null;
}

/**
 * What the applied song did to the Waves plugin, including WHY nothing
 * happened (operator, 2026-09-28: *"it would be useful to know that no key
 * has been sent/recieved when a song comes thru"*).
 *
 * Every non-`sent` kind except `unknown` is DEFINITE - read from what OPBridge
 * recorded for THIS song (or, for `no-key`, from the song itself, since
 * OPBridge records nothing for a keyless song). `unknown` covers everything
 * that cannot be said yet: nothing read, no song since OPBridge started, an
 * OPBridge older than S279, or `last` not yet this song's outcome (the few
 * milliseconds between a song change's frames, or a song adopted from a
 * peer bridge, whose outcome stays on that bridge). A held song never
 * reaches here: it is not the applied song. A surface
 * shows a reason only for the definite kinds, so the gap between frames can
 * never flash a false "not sent".
 *
 * - `no-key`      - the song carried no key, so OPBridge sent nothing.
 * - `unreadable`  - OPBridge could not read the key (`pitch_class` absent).
 * - `no-port`     - read, but no autotune MIDI port is set in OPBridge.
 * - `send-failed` - read, a port is set, and the send still did not happen
 *                   (the port is gone, or OPBridge's MIDI is not wired).
 */
export type SongAutotuneStatus =
  | { kind: 'sent'; key: string; pitchClass: number; mode: VocalsKeyMode }
  | { kind: 'no-key' }
  | { kind: 'unreadable'; key: string }
  | { kind: 'no-port'; key: string }
  | { kind: 'send-failed'; key: string }
  | { kind: 'unknown' };

export function songAutotuneStatus(m: SetlistMirror = mirrorSig()): SongAutotuneStatus {
  const song = appliedSong(m);
  if (!song) return { kind: 'unknown' };
  const raw = song.vocals_key;
  const key = typeof raw === 'string' ? rustTrim(raw) : '';
  if (key.length === 0) return { kind: 'no-key' };
  const last = m.autotune?.last;
  if (!last || last.vocals_key !== key) return { kind: 'unknown' };
  const pc = last.pitch_class;
  const mode = MODES.find((x) => x === last.mode);
  const parsed = typeof pc === 'number' && Number.isInteger(pc) && pc >= 0 && pc <= 11 && !!mode;
  if (!parsed) return { kind: 'unreadable', key };
  if (last.emitted !== true) {
    return m.autotune?.configured === false ? { kind: 'no-port', key } : { kind: 'send-failed', key };
  }
  return { kind: 'sent', key, pitchClass: pc, mode };
}

/** One line a novice can act on, for each definite reason nothing was sent.
 *  `null` for `sent` and `unknown`, which have nothing to explain. */
export function songAutotuneWhy(s: SongAutotuneStatus): string | null {
  switch (s.kind) {
    case 'no-key':      return 'this song has no key';
    case 'unreadable':  return `OPBridge could not read "${s.key}"`;
    case 'no-port':     return 'no autotune port set in OPBridge';
    case 'send-failed': return 'OPBridge could not reach the autotune port';
    default:            return null;
  }
}
